import React, {
  useEffect,
  useRef,
  useMemo,
} from "react";
import {
  StyledIconButton,
  StyledToggleButton,
  PrimaryButton,
  SecondaryButton,
  StyledToggleButtonGroup,
} from "../../components/StyledComponents/Buttons/AuradineButtons";
import FilterList from "@mui/icons-material/FilterList";
import Tooltip from "@mui/material/Tooltip";
import { useState } from "react";
import { StyledFormControl } from "../../components/StyledComponents/Inputs/Inputs.js";
import {
  StyledDialog,
  StyledDialogTitle,
} from "../../components/StyledComponents/Dialog/Dialog";
import {
  H6HeadlineSmall,
  H8TitleMedium,
} from "../../components/StyledComponents/Typography/Typography.tsx";
import {
  DialogContent,
  Autocomplete,
  Badge,
  styled,
  Tabs,
  Tab,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import CloseIcon from "@mui/icons-material/Close";
import { Box } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import PlaceHolderButton from "../../components/PlaceHolderButton/PlaceHolderButton";
import {
  StyledTextField,
  StyledPaper,
} from "../../components/StyledComponents/Inputs/Inputs.js";
import { useAuth0 } from "@auth0/auth0-react";
import { useQuery } from "@tanstack/react-query";
import {
  DateTimePicker,
  LocalizationProvider,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { GetCaseFilterListNames, PostAllCaseNumbers, GetCaseFilterList, PostAllCaseCreatedBy, PostAllCaseSerialNo, PostAllCaseUpdatedBy, PostCaseList } from "../../api/api.js";
import useAppContextProvider from "../../AppContext/useAppContextProvider.js";
import CaseManagementSavedLists from "./CaseManagementSavedLists.js";
import { useTheme } from "../../ThemeContext/ThemeContext.js";

export const caseStatusOptions = [
  "New",
  "Active",
  "Need More Information",
  "Information Provided",
  "Escalated",
  "Customer Action Required",
  "Pending Release",
  "RMA",
  "RMA In Progress",
  "Awaiting Customer Verification",
  "On Hold",
  "Closed"
];

const CustomBadge = styled(Badge)(({ colors }) => ({
  "& .MuiBadge-badge": {
    backgroundColor: colors?.primary?.main,
    color: colors?.primary?.contrastText,
    fontSize: "0.75rem",
    padding: "0.25rem",
  },
}));

const FilterModal = (props) => {
  const { open, setOpenFilter, setActiveFilters } = props;
  const { colors } = useTheme();
  const [caseNoInputValue, setCaseNoInputValue] = useState("");
  const [createdByInputValue, setCreatedByInputValue] = useState("");
  const [updatedByInputValue, setUpdatedByInputValue] = useState("");
  const [summaryInputValue, setSummaryInputValue] = useState("");
  const [firmwareInputValue, setFirmwareInputValue] = useState("");

  const [serialNoInputValue, setSerialNoInputValue] = useState("");

  const fieldRefs = useRef({});
  const [selectedTab, setSelectedTab] = useState(0);
  const [caseNoMode, setCaseNoMode] = useState("individual");

  const [serialNoMode, setSerialNoMode] = useState("individual");

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    getValues,
  } = useForm({
    defaultValues: {
      caseNo: [],
      caseNo1: "",
      caseNoListName: "",
      issue: [],
      serialNo: [],
      serialNo1: "",
      serialNoListName: "",
      caseStatus: [],
      createdBy: [],
      createdBy1: "",
      updatedBy: [],
      updatedBy1: "",
      summary: [],
      summary1: "",
      summaryListName: "",
      firmware: [],
      firmware1: "",
      firmwareListName: "",
      createdAtFrom: "",
      createdAtTo: "",
      updatedAtFrom: "",
      updatedAtTo: ""
    },
  });

  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  const { setToastMessage, setToastOpen } = useAppContextProvider();

  const {
    data: filterNames,
    refetch: refetchFilterNames,
    isLoading: areFilterNamesLoading,
  } = useQuery({
    queryKey: ["caseFilterListNames", open],
    queryFn: async () => {
      if (!isAuthenticated) return null;
      const token = await getAccessTokenSilently();
      const response = await GetCaseFilterListNames(token);
      return response.msg;
    },
    enabled: open,
    staleTime: 0,
  });



  const {
    data: caseNumbers,
  } = useQuery({
    queryKey: ["caseNumbers", open],
    queryFn: async () => {
      if (!isAuthenticated) return null;
      const token = await getAccessTokenSilently();
      const response = await PostAllCaseNumbers({
        authToken: token,
        body: {
          pagination: {
            limit: 100,
            skip: 0
          }
        }
      });
      return response.msg || [];
    },
    enabled: open,
    staleTime: 0,
  });

  const {
    data: summaryData,
  } = useQuery({
    queryKey: ["summaryData", open],
    queryFn: async () => {
      if (!isAuthenticated) return null;
      const token = await getAccessTokenSilently();
      const response = await PostCaseList({
        authToken: token,
        body: {
          filters: {},
          pagination: {
            limit: 1000,
            skip: 0
          }
        }
      });
      // Extract unique summary values from case data
      const summaries = response.msg
        ?.map(caseItem => caseItem.summary)
        .filter(summary => summary && summary.trim() !== "")
        .filter((summary, index, self) => self.indexOf(summary) === index) // Remove duplicates
        .sort() || [];
      return summaries;
    },
    enabled: open,
    staleTime: 0,
  });

  const {
    data: firmwareData,
  } = useQuery({
    queryKey: ["firmwareData", open],
    queryFn: async () => {
      if (!isAuthenticated) return null;
      const token = await getAccessTokenSilently();
      const response = await PostCaseList({
        authToken: token,
        body: {
          filters: {},
          pagination: {
            limit: 1000,
            skip: 0
          }
        }
      });
      // Extract unique firmware values from case data
      const firmwares = response.msg
        ?.map(caseItem => caseItem.firmware)
        .filter(firmware => firmware && firmware.trim() !== "" && firmware !== "NA")
        .filter((firmware, index, self) => self.indexOf(firmware) === index) // Remove duplicates
        .sort() || [];
      return firmwares;
    },
    enabled: open,
    staleTime: 0,
  });

  const {
    data: caseCreatedBy,
  } = useQuery({
    queryKey: ["caseCreatedBy", open],
    queryFn: async () => {
      if (!isAuthenticated) return null;
      const token = await getAccessTokenSilently();
      const response = await PostAllCaseCreatedBy({
        authToken: token,
        body: {
          pagination: {
            limit: 100,
            skip: 0
          }
        }
      });
      return response.msg || [];
    },
    enabled: open,
    staleTime: 0,
  });

  const {
    data: caseUpdatedBy,
  } = useQuery({
    queryKey: ["caseUpdatedBy", open],
    queryFn: async () => {
      if (!isAuthenticated) return null;
      const token = await getAccessTokenSilently();
      const response = await PostAllCaseUpdatedBy({
        authToken: token,
        body: {
          pagination: {
            limit: 100,
            skip: 0
          }
        }
      });
      return response.msg || [];
    },
    enabled: open,
    staleTime: 0,
  });

  const {
    data: caseSerialNo,
  } = useQuery({
    queryKey: ["caseSerialNo", open],
    queryFn: async () => {
      if (!isAuthenticated) return null;
      const token = await getAccessTokenSilently();
      const response = await PostAllCaseSerialNo({
        authToken: token,
        body: {
          pagination: {
            limit: 100,
            skip: 0
          }
        }
      });
      return response.msg || [];
    },
    enabled: open,
    staleTime: 0,
  });

  // Fetch case data for case number to case ID mapping
  const {
    data: caseDataForMapping,
  } = useQuery({
    queryKey: ["caseDataForMapping", open],
    queryFn: async () => {
      if (!isAuthenticated) return null;
      const token = await getAccessTokenSilently();
      const response = await PostCaseList({
        authToken: token,
        body: {
          filters: {},
          sort: {},
          pagination: {
            limit: 1000, // Get all cases for mapping
            skip: 0
          }
        }
      });
      return response.msg || [];
    },
    enabled: open,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  // Removed unused token fetching logic

  // Create mapping from case numbers to case IDs
  const createCaseNumberToIdMapping = (caseData) => {
    if (!caseData || !Array.isArray(caseData)) return {};

    const mapping = {};
    caseData.forEach(caseItem => {
      if (caseItem["Case Number"] && caseItem.caseId) {
        mapping[caseItem["Case Number"]] = caseItem.caseId;
      }
    });
    return mapping;
  };

  // Create the mapping when caseDataForMapping is available
  const caseNumberToIdMapping = useMemo(() => {
    return createCaseNumberToIdMapping(caseDataForMapping);
  }, [caseDataForMapping]);

  // when filter modal opens, gets the filters from url params,
  // reset(filter) to fill in inputs in form
  const getFiltersFromURL = () => {
    const params = new URLSearchParams(window.location.search);

    // Helper function to convert ISO string to dayjs object for DateTimePicker
    const convertToDayjs = (isoString) => {
      if (!isoString) return null;
      try {
        const dayjsDate = dayjs(isoString);
        return dayjsDate.isValid() ? dayjsDate : null;
      } catch (error) {
        console.error("Error converting date:", error);
        return null;
      }
    };

    return {
      caseNo: params.getAll("caseNo") || [],
      caseNo1: params.get("caseNo1") || "",
      caseNoListName: params.get("caseNoListName") || "",
      issue: params.getAll("issue") || [],
      serialNo: params.getAll("serialNo") || [],
      serialNo1: params.get("serialNo1") || "",
      serialNoListName: params.get("serialNoListName") || "",
      caseStatus: params.getAll("caseStatus") || [],
      createdBy: params.getAll("createdBy") || [],
      createdBy1: params.get("createdBy1") || "",
      updatedBy: params.getAll("updatedBy") || [],
      updatedBy1: params.get("updatedBy1") || "",
      summary: params.getAll("summary") || [],
      summary1: params.get("summary1") || "",
      summaryListName: params.get("summaryListName") || "",
      firmware: params.getAll("firmware") || [],
      firmware1: params.get("firmware1") || "",
      firmwareListName: params.get("firmwareListName") || "",
      createdAtFrom: convertToDayjs(params.get("createdAtFrom"))?.toISOString() || "",
      createdAtTo: convertToDayjs(params.get("createdAtTo"))?.toISOString() || "",
      updatedAtFrom: convertToDayjs(params.get("updatedAtFrom"))?.toISOString() || "",
      updatedAtTo: convertToDayjs(params.get("updatedAtTo"))?.toISOString() || ""
    };
  };

  useEffect(() => {
    if (open) {
      const filters = getFiltersFromURL();

      if (filters?.caseNoListName) {
        setCaseNoMode("saved");
      } else {
        setCaseNoMode("individual");
      }

      if (filters?.serialNoListName) {
        setSerialNoMode("saved");
      } else {
        setSerialNoMode("individual");
      }


      if (filters && filters?.caseNo1) {
        setCaseNoInputValue(filters?.caseNo1);
      } else {
        setCaseNoInputValue("");
      }
      if (filters && filters?.createdBy1) {
        setCreatedByInputValue(filters?.createdBy1);
      } else {
        setCreatedByInputValue("");
      }
      if (filters && filters?.updatedBy1) {
        setUpdatedByInputValue(filters?.updatedBy1);
      } else {
        setUpdatedByInputValue("");
      }
      if (filters && filters?.serialNo1) {
        setSerialNoInputValue(filters?.serialNo1);
      } else {
        setSerialNoInputValue("");
      }
      if (filters && filters?.summary1) {
        setSummaryInputValue(filters?.summary1);
      } else {
        setSummaryInputValue("");
      }
      if (filters && filters?.firmware1) {
        setFirmwareInputValue(filters?.firmware1);
      } else {
        setFirmwareInputValue("");
      }

      reset(filters);
    }
  }, [open, reset]);

  // Listen for caseFiltersChanged event to reset form when filters are cleared externally
  useEffect(() => {
    const handleFiltersChanged = (event) => {
      const newFilters = event.detail?.filters || {};

      // If filters are cleared (empty object), reset the form
      if (Object.keys(newFilters).length === 0) {
        const emptyFilters = {
          caseNo: [],
          caseNo1: "",
          caseNoListName: "",
          issue: [],
          serialNo: [],
          serialNo1: "",
          serialNoListName: "",
          caseStatus: [],
          createdBy: [],
          createdBy1: "",
          updatedBy: [],
          updatedBy1: "",
          summary: [],
          summary1: "",
          summaryListName: "",
          firmware: [],
          firmware1: "",
          firmwareListName: "",
          createdAtFrom: "",
          createdAtTo: "",
          updatedAtFrom: "",
          updatedAtTo: ""
        };

        reset(emptyFilters);
        setCaseNoInputValue("");
        setCreatedByInputValue("");
        setUpdatedByInputValue("");
        setSummaryInputValue("");
        setFirmwareInputValue("");
        setSerialNoInputValue("");
        setCaseNoMode("individual");
        setSerialNoMode("individual");
      } else {
        // If filters are set, update form with new values
        const filters = getFiltersFromURL();
        if (filters?.caseNoListName) {
          setCaseNoMode("saved");
        } else {
          setCaseNoMode("individual");
        }
        if (filters?.serialNoListName) {
          setSerialNoMode("saved");
        } else {
          setSerialNoMode("individual");
        }
        setCaseNoInputValue(filters?.caseNo1 || "");
        setCreatedByInputValue(filters?.createdBy1 || "");
        setUpdatedByInputValue(filters?.updatedBy1 || "");
        setSerialNoInputValue(filters?.serialNo1 || "");
        setSummaryInputValue(filters?.summary1 || "");
        setFirmwareInputValue(filters?.firmware1 || "");
        reset(filters);
      }
    };

    window.addEventListener('caseFiltersChanged', handleFiltersChanged);

    return () => {
      window.removeEventListener('caseFiltersChanged', handleFiltersChanged);
    };
  }, [reset]);

  // Watch form values and update active filters for badge count
  const formValues = watch();
  useEffect(() => {
    if (open && setActiveFilters) {
      const activeFiltersData = Object.fromEntries(
        Object.entries(formValues).filter(
          ([key, value]) =>
            !(
              value === undefined ||
              value === null ||
              value === "" ||
              (Array.isArray(value) && value.length === 0)
            )
        )
      );
      setActiveFilters(activeFiltersData);
    }
  }, [formValues, open, setActiveFilters]);

  // on click of apply button, updates filter and clears then updates url params
  const onSubmit = async (data) => {
    console.log('CaseManagementFilters - onSubmit called with data:', data);
    console.log('CaseManagementFilters - caseNo in data:', data.caseNo);
    console.log('CaseManagementFilters - caseNoListName in data:', data.caseNoListName);

    let caseIdArray = [];

    // Check if caseNoListName is selected (saved list)
    if (data.caseNoListName) {
      console.log('CaseManagementFilters - Case number saved list selected:', data.caseNoListName);

      try {
        const token = await getAccessTokenSilently();
        const response = await GetCaseFilterList(
          {
            filterType: "caseNo",
            filterListName: data.caseNoListName,
          },
          token
        );

        console.log('CaseManagementFilters - Fetched case numbers from saved list:', response.msg);

        if (response.status === "ok" && response.msg && Array.isArray(response.msg)) {
          // Map case numbers from saved list to case IDs
          caseIdArray = response.msg
            .map(caseNumber => {
              const caseId = caseNumberToIdMapping[caseNumber];
              console.log(`CaseManagementFilters - Mapping saved list case number "${caseNumber}" to "${caseId}"`);
              return caseId;
            })
            .filter(caseId => caseId); // Remove undefined mappings

          console.log('CaseManagementFilters - Mapped case IDs from saved list:', caseIdArray);
        }
      } catch (error) {
        console.error('CaseManagementFilters - Error fetching case numbers from saved list:', error);
        setToastMessage("Error fetching case numbers from saved list");
        setToastOpen(true);
        return;
      }
    }
    // Check if caseNo has any data and needs mapping (individual selection)
    else if (data.caseNo && Array.isArray(data.caseNo) && data.caseNo.length > 0) {
      console.log('CaseManagementFilters - Individual case numbers selected:', data.caseNo);
      console.log('CaseManagementFilters - Using pre-loaded mapping:', caseNumberToIdMapping);

      // Map selected case numbers to case IDs using pre-loaded data
      caseIdArray = data.caseNo
        .map(caseNumber => {
          const caseId = caseNumberToIdMapping[caseNumber];
          console.log(`CaseManagementFilters - Mapping individual case number "${caseNumber}" to "${caseId}"`);
          return caseId;
        })
        .filter(caseId => caseId); // Remove undefined mappings

      console.log('CaseManagementFilters - Final caseId array from individual selection:', caseIdArray);
    }

    const finalData =
      data && Object.keys(data).length > 0
        ? Object.fromEntries(
            Object.entries(data)
              .map(([key, value]) => {
                // Map caseNo to caseId first
                if (key === "caseNo" && Array.isArray(value) && value.length > 0) {
                  console.log('CaseManagementFilters - Processing caseNo array:', value);
                  console.log('CaseManagementFilters - Using mapped caseId array:', caseIdArray);
                  return ["caseId", caseIdArray];
                }

                // Handle array conversion for other fields
                if (["caseStatus", "issue", "serialNo", "createdBy", "updatedBy"].includes(key)) {
                  if (typeof value === "string") {
                    return [key, [value]];
                  }
                  if (Array.isArray(value)) {
                    return [key, value.map(String)];
                  }
                }

                return [key, value];
              })
              .filter(
                ([key, value]) => {
                  // Include caseId if we have mapped case IDs from saved list or individual selection
                  if (key === "caseId") {
                    return caseIdArray.length > 0; // Include caseId if we have mapped case IDs
                  }

                  // Exclude caseNoListName if we have successfully fetched and mapped case numbers
                  if (key === "caseNoListName" && caseIdArray.length > 0) {
                    return false; // Don't include caseNoListName if we have mapped case IDs
                  }

                  if (key === "serialNo" && data.serialNoListName) {
                    return false; // Don't include serialNo if serialNoListName is selected
                  }

                  return value !== null &&
                    value !== undefined &&
                    value !== "" &&
                    (!Array.isArray(value) || value.length > 0);
                }
              )
          )
        : [];

    // Add caseId array if we have mapped case IDs
    // Priority: caseId takes precedence over caseNo1 (mutually exclusive)
    if (caseIdArray.length > 0) {
      finalData["caseId"] = caseIdArray;
      console.log('CaseManagementFilters - Added caseId array to finalData:', caseIdArray);
    }

    // Handle caseNo1 field like chassis1 in Miner Filters
    // Only add caseNo1 if caseId is not present (mutually exclusive)
    if (
      caseNoMode === "individual" &&
      caseNoInputValue &&
      caseNoInputValue.trim() !== "" &&
      (!finalData["caseNo"] || finalData["caseNo"].length === 0) &&
      (!finalData["caseId"] || finalData["caseId"].length === 0)
    ) {
      finalData["caseNo1"] = caseNoInputValue;
      console.log('CaseManagementFilters - Added caseNo1 to finalData:', caseNoInputValue);
    } else if (
      caseNoMode === "individual" &&
      caseNoInputValue &&
      caseNoInputValue.trim() !== "" &&
      finalData["caseId"] &&
      finalData["caseId"].length > 0
    ) {
      console.log('CaseManagementFilters - Skipping caseNo1 because caseId is already present');
    }

    // Handle createdBy1 field for string input
    if (
      createdByInputValue &&
      createdByInputValue.trim() !== "" &&
      (!finalData["createdBy"] || finalData["createdBy"].length === 0)
    ) {
      finalData["createdBy1"] = createdByInputValue;
    }

    // Handle updatedBy1 field for string input
    if (
      updatedByInputValue &&
      updatedByInputValue.trim() !== "" &&
      (!finalData["updatedBy"] || finalData["updatedBy"].length === 0)
    ) {
      finalData["updatedBy1"] = updatedByInputValue;
    }

    // Handle serialNo1 field for string input
    if (
      serialNoInputValue &&
      serialNoInputValue.trim() !== "" &&
      (!finalData["serialNo"] || finalData["serialNo"].length === 0)
    ) {
      finalData["serialNo1"] = serialNoInputValue;
    }

    console.log('CaseManagementFilters - finalData after processing:', finalData);
    console.log('CaseManagementFilters - caseId in finalData:', finalData.caseId);
    console.log('CaseManagementFilters - caseNo in finalData:', finalData.caseNo);
    console.log('CaseManagementFilters - caseNoListName in finalData:', finalData.caseNoListName);
    console.log('CaseManagementFilters - caseIdArray length:', caseIdArray.length);

    sessionStorage.setItem("caseFilters", JSON.stringify(finalData));

    const url = new URL(window.location.href);
    url.search = "";

    for (const key in finalData) {
      if (Array.isArray(finalData[key])) {
        finalData[key].forEach((value) =>
          url.searchParams.append(key, value)
        );
      } else {
        if (finalData[key] !== "") {
          url.searchParams.set(key, finalData[key]);
        }
      }
    }

    window.history.replaceState({}, "", url);

    // Dispatch custom event to notify that filters have changed
    console.log('CaseManagementFilters - Dispatching caseFiltersChanged event with finalData:', finalData);
    window.dispatchEvent(new CustomEvent('caseFiltersChanged', {
      detail: { filters: finalData }
    }));

    setOpenFilter(false);
  };

  const ClearFilters = () => {
    reset({
      caseNo: [],
      caseNo1: "",
      caseNoListName: "",
      issue: [],
      issue1: "",

      serialNo: [],
      serialNo1: "",
      serialNoListName: "",
      caseStatus: [],
      createdBy: [],
      createdBy1: "",
      updatedBy: [],
      updatedBy1: "",
      summary: [],
      summary1: "",
      summaryListName: "",
      firmware: [],
      firmware1: "",
      firmwareListName: "",
      createdAtFrom: "",
      createdAtTo: "",
      updatedAtFrom: "",
      updatedAtTo: ""
    });

    const url = new URL(window.location.href);
    url.search = "";
    window.history.replaceState({}, "", url);

    setCaseNoInputValue("");
    setCreatedByInputValue("");
    setUpdatedByInputValue("");
    setSummaryInputValue("");
    setFirmwareInputValue("");

    setSerialNoInputValue("");

    sessionStorage.setItem("caseFilters", JSON.stringify({}));
  };

  const onError = (errors) => {
    const firstErrorField = Object.keys(errors)[0];
    const fieldElement = fieldRefs.current[firstErrorField];

    if (fieldElement) {
      fieldElement.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      fieldElement.focus();
    }
  };


  const issueOptions = [
    "Administration/Management",
    "API",
    "Power Supply",
    "Fans Chassis",
    "PSU Fans",
    "Control Board",
    "Hash Board",
    "Configuration",
    "Network/Connectivity",
    "Performance",
    "Tuning",
    "Miner UI",
    "FluxVision",
    "ZTP",
    "Chassis/System",
    "General Query"
  ];

  const selectedCaseStatus = watch("caseStatus");
  const selectedCaseNo = watch("caseNo");
  const selectedIssue = watch("issue");
  const selectedSerialNo = watch("serialNo");
  const selectedCaseNoListName = watch("caseNoListName");
  const selectedSerialNoListName = watch("serialNoListName");
  const selectedSummary = watch("summary");
  const selectedSummaryListName = watch("summaryListName");
  const selectedFirmware = watch("firmware");
  const selectedFirmwareListName = watch("firmwareListName");

  const filterManagement = filterNames || {};

  const caseNoListNames = filterManagement["caseNo"] || [];

  const serialNoListNames = filterManagement["serialNo"] || [];



  const handleCaseStatusAutoCompleteDeletion = (optionToDelete) => {
    const newValue = selectedCaseStatus.filter(
      (option) => option !== optionToDelete
    );
    setValue("caseStatus", newValue);
  };

  const handleCaseNoAutoCompleteDeletion = (optionToDelete) => {
    if (Array.isArray(selectedCaseNo)) {
      const newValue = selectedCaseNo.filter(
        (option) => option !== optionToDelete
      );
      setValue("caseNo", newValue);
    } else {
      setValue("caseNo", "");
    }
  };

  const handleIssueAutoCompleteDeletion = (optionToDelete) => {
    const newValue = selectedIssue.filter(
      (option) => option !== optionToDelete
    );
    setValue("issue", newValue);
  };



  const handleCreatedByAutoCompleteDeletion = (optionToDelete) => {
    const selectedCreatedBy = watch("createdBy");
    if (Array.isArray(selectedCreatedBy)) {
      const newValue = selectedCreatedBy.filter(
        (option) => option !== optionToDelete
      );
      setValue("createdBy", newValue);
    } else {
      setValue("createdBy", []);
    }
  };

  const handleUpdatedByAutoCompleteDeletion = (optionToDelete) => {
    const selectedUpdatedBy = watch("updatedBy");
    if (Array.isArray(selectedUpdatedBy)) {
      const newValue = selectedUpdatedBy.filter(
        (option) => option !== optionToDelete
      );
      setValue("updatedBy", newValue);
    } else {
      setValue("updatedBy", []);
    }
  };

  const handleSummaryAutoCompleteDeletion = (optionToDelete) => {
    const selectedSummary = watch("summary");
    if (Array.isArray(selectedSummary)) {
      const newValue = selectedSummary.filter(
        (option) => option !== optionToDelete
      );
      setValue("summary", newValue);
    } else {
      setValue("summary", []);
    }
  };

  const handleFirmwareAutoCompleteDeletion = (optionToDelete) => {
    const selectedFirmware = watch("firmware");
    if (Array.isArray(selectedFirmware)) {
      const newValue = selectedFirmware.filter(
        (option) => option !== optionToDelete
      );
      setValue("firmware", newValue);
    } else {
      setValue("firmware", []);
    }
  };

  const handleSerialNoAutoCompleteDeletion = (optionToDelete) => {
    const selectedSerialNo = watch("serialNo");
    if (Array.isArray(selectedSerialNo)) {
      const newValue = selectedSerialNo.filter(
        (option) => option !== optionToDelete
      );
      setValue("serialNo", newValue);
    } else {
      setValue("serialNo", []);
    }
  };

  // Load saved list content when list name changes
  useEffect(() => {
    const loadSavedListContent = async (listName, filterType) => {
      if (!listName || !isAuthenticated) return;

      try {
        const token = await getAccessTokenSilently();
        const response = await GetCaseFilterList(
          { filterListName: listName, filterType },
          token
        );

        if (response.status === "ok" && response.msg) {
          if (filterType === "caseNo") {
            setValue("caseNo", response.msg);
          }
        }
      } catch (error) {
        console.error("Error loading saved list content:", error);
      }
    };

    if (selectedCaseNoListName) {
      loadSavedListContent(selectedCaseNoListName, "caseNo");
    }


      if (selectedSerialNoListName) {
        loadSavedListContent(selectedSerialNoListName, "serialNo");
      }
    }, [selectedCaseNoListName, selectedSerialNoListName, isAuthenticated, getAccessTokenSilently, setValue]);

  return (
    <StyledDialog
      colors={colors}
      disableScrollLock
      TransitionProps={{ timeout: 0 }}
      maxWidth={"lg"}
      open={open}
      onClose={(event, reason) => {
        if (reason === "backdropClick") {
          setOpenFilter(false);
          setSelectedTab(0);
        }
      }}
      hideBackdrop
    >
      <StyledDialogTitle sx={{ paddingTop: 0 }}>
        <Box
          display={"flex"}
          flexDirection={"row"}
          justifyContent={"space-between"}
        >
          <H6HeadlineSmall style={{ textAlign: "left" }}>
            Case Management Filters
          </H6HeadlineSmall>
          <StyledIconButton
            colors={colors}
            onClick={() => {
              setOpenFilter(false);
              setSelectedTab(0);
            }}
            style={{ textAlign: "right" }}
          >
            <CloseIcon />
          </StyledIconButton>
        </Box>
      </StyledDialogTitle>
      <DialogContent sx={{ padding: 0 }}>
        <Tabs
          value={selectedTab}
          onChange={(event, newValue) => setSelectedTab(newValue)}
          variant="fullWidth"
          textColor="inherit"
          indicatorColor="primary"
          TabIndicatorProps={{
            children: <span className="MuiTabs-indicatorSpan" />,
          }}
          sx={{
            "& .MuiTab-root": {
              textTransform: "none",
              color: colors.text.label,
              fontFamily: "Roboto",
              fontSize: "1rem",
              fontWeight: "500",
              lineHeight: "1.25rem",
            },
            "& .Mui-selected": {
              color: colors.primary.main, // Use theme primary color for selected tab
            },
            "& .MuiTabs-indicator": {
              display: "flex",
              justifyContent: "center",
              height: "4px",
              backgroundColor: "transparent",
            },
            "& .MuiTabs-indicatorSpan": {
              backgroundColor: colors.primary.main,
              borderTopLeftRadius: 100,
              width: "25%",
              borderTopRightRadius: 100,
            },
          }}
        >
          <Tab label="Filters" />
          <Tab label="Saved Lists" />
        </Tabs>
        {selectedTab === 0 && (
          <Box sx={{ paddingTop: "1rem" }}>
            <form onSubmit={handleSubmit(onSubmit, onError)}>
              <Box
                display={"flex"}
                flexDirection={"column"}
                gap={8}
                maxHeight={"60vh"}
                overflow={"auto"}
                sx={{
                  scrollbarWidth: "none",
                  overflowAnchor: "none",
                }}
              >
                <Box
                  display={"flex"}
                  flexDirection={"column"}
                  gap={4}
                  className="createdAtContainer"
                >
                  <H8TitleMedium>Created At</H8TitleMedium>
                  <Box
                    display="flex"
                    flexDirection="row"
                    gap={6}
                    flexGrow={1}
                  >
                    <Controller
                      name="createdAtFrom"
                      control={control}
                      defaultValue=""
                      render={({ field }) => (
                        <StyledFormControl colors={colors} style={{ flex: 1 }}>
                          <LocalizationProvider
                            dateAdapter={AdapterDayjs}
                          >
                            <DateTimePicker
                          label="From"
                              value={field.value ? dayjs(field.value) : null}
                              onChange={(date) =>
                                field.onChange(date ? date.toISOString() : "")
                              }
                              slots={{
                                desktopPaper: StyledPaper,
                              }}
                              slotProps={{
                                popper: {
                                  sx: {
                                    "& .MuiPaper-root": {
                                      backgroundColor: colors?.background?.paper,
                                    },
                                  },
                                },
                                desktopPaper: {
                                  sx: {
                                    backgroundColor: colors?.background?.paper,
                                    borderRadius: "12px",
                                    boxShadow: "0px 4px 20px rgba(0,0,0,0.12)",

                                    "& .MuiPickersCalendarHeader-label": {
                                      color: colors?.text?.primary,
                                    },

                                    "& .MuiPickersCalendarHeader-switchViewIcon": {
                                      color: colors?.primary?.main,
                                      "&:hover": { color: colors?.primary?.dark },
                                    },

                                    "& .MuiPickersArrowSwitcher-root .MuiIconButton-root": {
                                      color: colors?.primary?.main,
                                      "&:hover": { color: colors?.primary?.dark },
                                    },

                                    "& .MuiDayCalendar-weekDayLabel": {
                                      color: colors?.text?.secondary,
                                    },

                                    "& .MuiPickersDay-root": {
                                      color: colors?.text?.primary,
                                      "&:hover": {
                                        backgroundColor: colors?.hover?.muted,
                                      },
                                    },

                                    "& .MuiPickersDay-root.Mui-disabled": {
                                      color: colors?.text?.muted,
                                    },

                                    "& .MuiPickersDay-root.MuiPickersDay-today": {
                                      border: `1px solid ${colors?.text?.muted}`,
                                    },

                                    "& .MuiPickersDay-root.Mui-selected": {
                                      backgroundColor: colors?.primary?.main,
                                      color: "#fff",
                                      borderRadius: "1rem",
                                      "&:hover": {
                                        backgroundColor: colors?.hover?.primary,
                                      },
                                    },

                                    "& .MuiYearCalendar-root .MuiPickersYear-root": {
                                      color: colors?.text?.primary,
                                      "&:hover": {
                                        backgroundColor: colors?.hover?.muted,
                                      },
                                    },

                                    "& .MuiYearCalendar-root .MuiPickersYear-yearButton.Mui-disabled": {
                                      color: colors?.text?.muted,
                                    },

                                    "& .MuiYearCalendar-root .MuiPickersYear-root.Mui-selected": {
                                      backgroundColor: colors?.primary?.main,
                                      color: "#fff",
                                      "&:hover": {
                                        backgroundColor: colors?.hover?.primary,
                                      },
                                    },

                                    "& .MuiPickersLayout-root .MuiPickersDigitalClock-root": {
                                      color: colors?.text?.primary,
                                    },

                                    "& .MuiPickersLayout-root .Mui-selected": {
                                      backgroundColor: colors?.primary?.main,
                                      "&:hover": {
                                        backgroundColor: colors?.hover?.primary,
                                      },
                                    },

                                    "& .MuiPickersLayout-root": {
                                      color: colors?.text?.muted,
                                    },

                                    "& .MuiPickersToolbar-root .MuiButtonBase-root": {
                                      color: colors?.text?.primary,
                                      "&:hover": { color: colors?.primary?.main },
                                    },

                                    "& .MuiPickersToolbar-root .Mui-selected": {
                                      backgroundColor: colors?.primary?.main,
                                      color: "#fff",
                                      "&:hover": {
                                        backgroundColor: colors?.hover?.primary,
                                      },
                                    },

                                    "& .MuiDialogActions-root button": {
                                      color: colors?.primary?.main,
                                      fontWeight: 600,
                                      "&:hover": { color: colors?.primary?.dark },
                                    },
                                  },
                                },
                                textField: {
                                  sx: {
                                    "& .MuiInputLabel-root, & .MuiInputLabel-root.Mui-focused": {
                                      color: colors?.text?.muted,
                                    },
                                    "& .MuiOutlinedInput-root .MuiOutlinedInput-input": {
                                      color: colors?.text?.muted,
                                    },
                                    "& .MuiOutlinedInput-root .MuiInputAdornment-root, & .MuiSvgIcon-root": {
                                      color: colors?.text?.muted,
                                      "&:hover": { color: colors?.primary?.main },
                                    },
                                    "& .MuiFormHelperText-root": {
                                      color: colors?.text?.muted,
                                    },
                                  },
                                  inputProps: {
                                    style: { color: colors?.text?.primary },
                                    placeholder: "MM/DD/YYYY at HH:MM am/pm",
                                  },
                                },
                                day: {
                                  sx: {
                                    "&.MuiPickersDay-root.Mui-selected": {
                                      backgroundColor: colors.primary.main,
                                      "&:hover": {
                                        backgroundColor: colors.hover?.primary,
                                      },
                                    },
                                    "&.Mui-disabled": {
                                      WebkitTextFillColor: `${colors?.text?.hint} !important`,
                                    },
                                  },
                                },
                                digitalClockSectionItem: {
                                  sx: {
                                    "&.Mui-selected": {
                                      backgroundColor: colors.primary.main,
                                      borderRadius: "50%",
                                      "&:hover": {
                                        backgroundColor: colors.hover?.primary,
                                      },
                                    },
                                    "&:hover": {
                                      backgroundColor: colors.hover?.muted,
                                    },
                                  },
                                },
                              }}
                              renderInput={(props) => (
                                <StyledTextField colors={colors}
                                  {...props}
                                  variant="outlined"
                          inputRef={(element) =>
                            (fieldRefs.current["createdAtFrom"] = element)
                          }
                        />
                              )}
                            />
                          </LocalizationProvider>
                        </StyledFormControl>
                      )}
                    />
                    <Controller
                      name="createdAtTo"
                      control={control}
                      defaultValue=""
                      render={({ field }) => (
                        <StyledFormControl colors={colors} style={{ flex: 1 }}>
                          <LocalizationProvider
                            dateAdapter={AdapterDayjs}
                          >
                            <DateTimePicker
                          label="To"
                              value={field.value ? dayjs(field.value) : null}
                              onChange={(date) =>
                                field.onChange(date ? date.toISOString() : "")
                              }
                              slots={{
                                desktopPaper: StyledPaper,
                              }}
                              slotProps={{
                                popper: {
                                  sx: {
                                    "& .MuiPaper-root": {
                                      backgroundColor: colors?.background?.paper,
                                    },
                                  },
                                },
                                desktopPaper: {
                                  sx: {
                                    backgroundColor: colors?.background?.paper,
                                    borderRadius: "12px",
                                    boxShadow: "0px 4px 20px rgba(0,0,0,0.12)",

                                    "& .MuiPickersCalendarHeader-label": {
                                      color: colors?.text?.primary,
                                    },

                                    "& .MuiPickersCalendarHeader-switchViewIcon": {
                                      color: colors?.primary?.main,
                                      "&:hover": { color: colors?.primary?.dark },
                                    },

                                    "& .MuiPickersArrowSwitcher-root .MuiIconButton-root": {
                                      color: colors?.primary?.main,
                                      "&:hover": { color: colors?.primary?.dark },
                                    },

                                    "& .MuiDayCalendar-weekDayLabel": {
                                      color: colors?.text?.secondary,
                                    },

                                    "& .MuiPickersDay-root": {
                                      color: colors?.text?.primary,
                                      "&:hover": {
                                        backgroundColor: colors?.hover?.muted,
                                      },
                                    },

                                    "& .MuiPickersDay-root.Mui-disabled": {
                                      color: colors?.text?.muted,
                                    },

                                    "& .MuiPickersDay-root.MuiPickersDay-today": {
                                      border: `1px solid ${colors?.text?.muted}`,
                                    },

                                    "& .MuiPickersDay-root.Mui-selected": {
                                      backgroundColor: colors?.primary?.main,
                                      color: "#fff",
                                      borderRadius: "1rem",
                                      "&:hover": {
                                        backgroundColor: colors?.hover?.primary,
                                      },
                                    },

                                    "& .MuiYearCalendar-root .MuiPickersYear-root": {
                                      color: colors?.text?.primary,
                                      "&:hover": {
                                        backgroundColor: colors?.hover?.muted,
                                      },
                                    },

                                    "& .MuiYearCalendar-root .MuiPickersYear-yearButton.Mui-disabled": {
                                      color: colors?.text?.muted,
                                    },

                                    "& .MuiYearCalendar-root .MuiPickersYear-root.Mui-selected": {
                                      backgroundColor: colors?.primary?.main,
                                      color: "#fff",
                                      "&:hover": {
                                        backgroundColor: colors?.hover?.primary,
                                      },
                                    },

                                    "& .MuiPickersLayout-root .MuiPickersDigitalClock-root": {
                                      color: colors?.text?.primary,
                                    },

                                    "& .MuiPickersLayout-root .Mui-selected": {
                                      backgroundColor: colors?.primary?.main,
                                      "&:hover": {
                                        backgroundColor: colors?.hover?.primary,
                                      },
                                    },

                                    "& .MuiPickersLayout-root": {
                                      color: colors?.text?.muted,
                                    },

                                    "& .MuiPickersToolbar-root .MuiButtonBase-root": {
                                      color: colors?.text?.primary,
                                      "&:hover": { color: colors?.primary?.main },
                                    },

                                    "& .MuiPickersToolbar-root .Mui-selected": {
                                      backgroundColor: colors?.primary?.main,
                                      color: "#fff",
                                      "&:hover": {
                                        backgroundColor: colors?.hover?.primary,
                                      },
                                    },

                                    "& .MuiDialogActions-root button": {
                                      color: colors?.primary?.main,
                                      fontWeight: 600,
                                      "&:hover": { color: colors?.primary?.dark },
                                    },
                                  },
                                },
                                textField: {
                                  sx: {
                                    "& .MuiInputLabel-root, & .MuiInputLabel-root.Mui-focused": {
                                      color: colors?.text?.muted,
                                    },
                                    "& .MuiOutlinedInput-root .MuiOutlinedInput-input": {
                                      color: colors?.text?.muted,
                                    },
                                    "& .MuiOutlinedInput-root .MuiInputAdornment-root, & .MuiSvgIcon-root": {
                                      color: colors?.text?.muted,
                                      "&:hover": { color: colors?.primary?.main },
                                    },
                                    "& .MuiFormHelperText-root": {
                                      color: colors?.text?.muted,
                                    },
                                  },
                                  inputProps: {
                                    style: { color: colors?.text?.primary },
                                    placeholder: "MM/DD/YYYY at HH:MM am/pm",
                                  },
                                },
                                day: {
                                  sx: {
                                    "&.MuiPickersDay-root.Mui-selected": {
                                      backgroundColor: colors.primary.main,
                                      "&:hover": {
                                        backgroundColor: colors.hover?.primary,
                                      },
                                    },
                                    "&.Mui-disabled": {
                                      WebkitTextFillColor: `${colors?.text?.hint} !important`,
                                    },
                                  },
                                },
                                digitalClockSectionItem: {
                                  sx: {
                                    "&.Mui-selected": {
                                      backgroundColor: colors.primary.main,
                                      borderRadius: "50%",
                                      "&:hover": {
                                        backgroundColor: colors.hover?.primary,
                                      },
                                    },
                                    "&:hover": {
                                      backgroundColor: colors.hover?.muted,
                                    },
                                  },
                                },
                              }}
                              renderInput={(props) => (
                                <StyledTextField colors={colors}
                                  {...props}
                                  variant="outlined"
                          inputRef={(element) =>
                            (fieldRefs.current["createdAtTo"] = element)
                          }
                        />
                              )}
                            />
                          </LocalizationProvider>
                        </StyledFormControl>
                      )}
                    />
                  </Box>
                </Box>

                <Box
                  display={"flex"}
                  flexDirection={"column"}
                  gap={4}
                  className="updatedAtContainer"
                >
                  <H8TitleMedium>Updated At</H8TitleMedium>
                  <Box
                    display="flex"
                    flexDirection="row"
                    gap={6}
                    flexGrow={1}
                  >
                    <Controller
                      name="updatedAtFrom"
                      control={control}
                      defaultValue=""
                      render={({ field }) => (
                        <StyledFormControl colors={colors} style={{ flex: 1 }}>
                          <LocalizationProvider
                            dateAdapter={AdapterDayjs}
                          >
                            <DateTimePicker
                          label="From"
                              value={field.value ? dayjs(field.value) : null}
                              onChange={(date) =>
                                field.onChange(date ? date.toISOString() : "")
                              }
                              slots={{
                                desktopPaper: StyledPaper,
                              }}
                              slotProps={{
                                popper: {
                                  sx: {
                                    "& .MuiPaper-root": {
                                      backgroundColor: colors?.background?.paper,
                                    },
                                  },
                                },
                                desktopPaper: {
                                  sx: {
                                    backgroundColor: colors?.background?.paper,
                                    borderRadius: "12px",
                                    boxShadow: "0px 4px 20px rgba(0,0,0,0.12)",

                                    "& .MuiPickersCalendarHeader-label": {
                                      color: colors?.text?.primary,
                                    },

                                    "& .MuiPickersCalendarHeader-switchViewIcon": {
                                      color: colors?.primary?.main,
                                      "&:hover": { color: colors?.primary?.dark },
                                    },

                                    "& .MuiPickersArrowSwitcher-root .MuiIconButton-root": {
                                      color: colors?.primary?.main,
                                      "&:hover": { color: colors?.primary?.dark },
                                    },

                                    "& .MuiDayCalendar-weekDayLabel": {
                                      color: colors?.text?.secondary,
                                    },

                                    "& .MuiPickersDay-root": {
                                      color: colors?.text?.primary,
                                      "&:hover": {
                                        backgroundColor: colors?.hover?.muted,
                                      },
                                    },

                                    "& .MuiPickersDay-root.Mui-disabled": {
                                      color: colors?.text?.muted,
                                    },

                                    "& .MuiPickersDay-root.MuiPickersDay-today": {
                                      border: `1px solid ${colors?.text?.muted}`,
                                    },

                                    "& .MuiPickersDay-root.Mui-selected": {
                                      backgroundColor: colors?.primary?.main,
                                      color: "#fff",
                                      borderRadius: "1rem",
                                      "&:hover": {
                                        backgroundColor: colors?.hover?.primary,
                                      },
                                    },

                                    "& .MuiYearCalendar-root .MuiPickersYear-root": {
                                      color: colors?.text?.primary,
                                      "&:hover": {
                                        backgroundColor: colors?.hover?.muted,
                                      },
                                    },

                                    "& .MuiYearCalendar-root .MuiPickersYear-yearButton.Mui-disabled": {
                                      color: colors?.text?.muted,
                                    },

                                    "& .MuiYearCalendar-root .MuiPickersYear-root.Mui-selected": {
                                      backgroundColor: colors?.primary?.main,
                                      color: "#fff",
                                      "&:hover": {
                                        backgroundColor: colors?.hover?.primary,
                                      },
                                    },

                                    "& .MuiPickersLayout-root .MuiPickersDigitalClock-root": {
                                      color: colors?.text?.primary,
                                    },

                                    "& .MuiPickersLayout-root .Mui-selected": {
                                      backgroundColor: colors?.primary?.main,
                                      "&:hover": {
                                        backgroundColor: colors?.hover?.primary,
                                      },
                                    },

                                    "& .MuiPickersLayout-root": {
                                      color: colors?.text?.muted,
                                    },

                                    "& .MuiPickersToolbar-root .MuiButtonBase-root": {
                                      color: colors?.text?.primary,
                                      "&:hover": { color: colors?.primary?.main },
                                    },

                                    "& .MuiPickersToolbar-root .Mui-selected": {
                                      backgroundColor: colors?.primary?.main,
                                      color: "#fff",
                                      "&:hover": {
                                        backgroundColor: colors?.hover?.primary,
                                      },
                                    },

                                    "& .MuiDialogActions-root button": {
                                      color: colors?.primary?.main,
                                      fontWeight: 600,
                                      "&:hover": { color: colors?.primary?.dark },
                                    },
                                  },
                                },
                                textField: {
                                  sx: {
                                    "& .MuiInputLabel-root, & .MuiInputLabel-root.Mui-focused": {
                                      color: colors?.text?.muted,
                                    },
                                    "& .MuiOutlinedInput-root .MuiOutlinedInput-input": {
                                      color: colors?.text?.muted,
                                    },
                                    "& .MuiOutlinedInput-root .MuiInputAdornment-root, & .MuiSvgIcon-root": {
                                      color: colors?.text?.muted,
                                      "&:hover": { color: colors?.primary?.main },
                                    },
                                    "& .MuiFormHelperText-root": {
                                      color: colors?.text?.muted,
                                    },
                                  },
                                  inputProps: {
                                    style: { color: colors?.text?.primary },
                                    placeholder: "MM/DD/YYYY at HH:MM am/pm",
                                  },
                                },
                                day: {
                                  sx: {
                                    "&.MuiPickersDay-root.Mui-selected": {
                                      backgroundColor: colors.primary.main,
                                      "&:hover": {
                                        backgroundColor: colors.hover?.primary,
                                      },
                                    },
                                    "&.Mui-disabled": {
                                      WebkitTextFillColor: `${colors?.text?.hint} !important`,
                                    },
                                  },
                                },
                                digitalClockSectionItem: {
                                  sx: {
                                    "&.Mui-selected": {
                                      backgroundColor: colors.primary.main,
                                      borderRadius: "50%",
                                      "&:hover": {
                                        backgroundColor: colors.hover?.primary,
                                      },
                                    },
                                    "&:hover": {
                                      backgroundColor: colors.hover?.muted,
                                    },
                                  },
                                },
                              }}
                              renderInput={(props) => (
                                <StyledTextField colors={colors}
                                  {...props}
                                  variant="outlined"
                          inputRef={(element) =>
                            (fieldRefs.current["updatedAtFrom"] = element)
                          }
                        />
                              )}
                            />
                          </LocalizationProvider>
                        </StyledFormControl>
                      )}
                    />
                    <Controller
                      name="updatedAtTo"
                      control={control}
                      defaultValue=""
                      render={({ field }) => (
                        <StyledFormControl colors={colors} style={{ flex: 1 }}>
                          <LocalizationProvider
                            dateAdapter={AdapterDayjs}
                          >
                            <DateTimePicker
                          label="To"
                              value={field.value ? dayjs(field.value) : null}
                              onChange={(date) =>
                                field.onChange(date ? date.toISOString() : "")
                              }
                              slots={{
                                desktopPaper: StyledPaper,
                              }}
                              slotProps={{
                                popper: {
                                  sx: {
                                    "& .MuiPaper-root": {
                                      backgroundColor: colors?.background?.paper,
                                    },
                                  },
                                },
                                desktopPaper: {
                                  sx: {
                                    backgroundColor: colors?.background?.paper,
                                    borderRadius: "12px",
                                    boxShadow: "0px 4px 20px rgba(0,0,0,0.12)",

                                    "& .MuiPickersCalendarHeader-label": {
                                      color: colors?.text?.primary,
                                    },

                                    "& .MuiPickersCalendarHeader-switchViewIcon": {
                                      color: colors?.primary?.main,
                                      "&:hover": { color: colors?.primary?.dark },
                                    },

                                    "& .MuiPickersArrowSwitcher-root .MuiIconButton-root": {
                                      color: colors?.primary?.main,
                                      "&:hover": { color: colors?.primary?.dark },
                                    },

                                    "& .MuiDayCalendar-weekDayLabel": {
                                      color: colors?.text?.secondary,
                                    },

                                    "& .MuiPickersDay-root": {
                                      color: colors?.text?.primary,
                                      "&:hover": {
                                        backgroundColor: colors?.hover?.muted,
                                      },
                                    },

                                    "& .MuiPickersDay-root.Mui-disabled": {
                                      color: colors?.text?.muted,
                                    },

                                    "& .MuiPickersDay-root.MuiPickersDay-today": {
                                      border: `1px solid ${colors?.text?.muted}`,
                                    },

                                    "& .MuiPickersDay-root.Mui-selected": {
                                      backgroundColor: colors?.primary?.main,
                                      color: "#fff",
                                      borderRadius: "1rem",
                                      "&:hover": {
                                        backgroundColor: colors?.hover?.primary,
                                      },
                                    },

                                    "& .MuiYearCalendar-root .MuiPickersYear-root": {
                                      color: colors?.text?.primary,
                                      "&:hover": {
                                        backgroundColor: colors?.hover?.muted,
                                      },
                                    },

                                    "& .MuiYearCalendar-root .MuiPickersYear-yearButton.Mui-disabled": {
                                      color: colors?.text?.muted,
                                    },

                                    "& .MuiYearCalendar-root .MuiPickersYear-root.Mui-selected": {
                                      backgroundColor: colors?.primary?.main,
                                      color: "#fff",
                                      "&:hover": {
                                        backgroundColor: colors?.hover?.primary,
                                      },
                                    },

                                    "& .MuiPickersLayout-root .MuiPickersDigitalClock-root": {
                                      color: colors?.text?.primary,
                                    },

                                    "& .MuiPickersLayout-root .Mui-selected": {
                                      backgroundColor: colors?.primary?.main,
                                      "&:hover": {
                                        backgroundColor: colors?.hover?.primary,
                                      },
                                    },

                                    "& .MuiPickersLayout-root": {
                                      color: colors?.text?.muted,
                                    },

                                    "& .MuiPickersToolbar-root .MuiButtonBase-root": {
                                      color: colors?.text?.primary,
                                      "&:hover": { color: colors?.primary?.main },
                                    },

                                    "& .MuiPickersToolbar-root .Mui-selected": {
                                      backgroundColor: colors?.primary?.main,
                                      color: "#fff",
                                      "&:hover": {
                                        backgroundColor: colors?.hover?.primary,
                                      },
                                    },

                                    "& .MuiDialogActions-root button": {
                                      color: colors?.primary?.main,
                                      fontWeight: 600,
                                      "&:hover": { color: colors?.primary?.dark },
                                    },
                                  },
                                },
                                textField: {
                                  sx: {
                                    "& .MuiInputLabel-root, & .MuiInputLabel-root.Mui-focused": {
                                      color: colors?.text?.muted,
                                    },
                                    "& .MuiOutlinedInput-root .MuiOutlinedInput-input": {
                                      color: colors?.text?.muted,
                                    },
                                    "& .MuiOutlinedInput-root .MuiInputAdornment-root, & .MuiSvgIcon-root": {
                                      color: colors?.text?.muted,
                                      "&:hover": { color: colors?.primary?.main },
                                    },
                                    "& .MuiFormHelperText-root": {
                                      color: colors?.text?.muted,
                                    },
                                  },
                                  inputProps: {
                                    style: { color: colors?.text?.primary },
                                    placeholder: "MM/DD/YYYY at HH:MM am/pm",
                                  },
                                },
                                day: {
                                  sx: {
                                    "&.MuiPickersDay-root.Mui-selected": {
                                      backgroundColor: colors.primary.main,
                                      "&:hover": {
                                        backgroundColor: colors.hover?.primary,
                                      },
                                    },
                                    "&.Mui-disabled": {
                                      WebkitTextFillColor: `${colors?.text?.hint} !important`,
                                    },
                                  },
                                },
                                digitalClockSectionItem: {
                                  sx: {
                                    "&.Mui-selected": {
                                      backgroundColor: colors.primary.main,
                                      borderRadius: "50%",
                                      "&:hover": {
                                        backgroundColor: colors.hover?.primary,
                                      },
                                    },
                                    "&:hover": {
                                      backgroundColor: colors.hover?.muted,
                                    },
                                  },
                                },
                              }}
                              renderInput={(props) => (
                                <StyledTextField colors={colors}
                                  {...props}
                                  variant="outlined"
                          inputRef={(element) =>
                            (fieldRefs.current["updatedAtTo"] = element)
                          }
                        />
                              )}
                            />
                          </LocalizationProvider>
                        </StyledFormControl>
                      )}
                    />
                  </Box>
                </Box>

                <Box
                  display={"flex"}
                  flexDirection={"column"}
                  gap={4}
                  className="caseNoContainer"
                >
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <H8TitleMedium>Case Number</H8TitleMedium>
                    <StyledToggleButtonGroup
                      colors={colors}
                      value={caseNoMode}
                      exclusive
                      size="small"
                      onChange={(e, newValue) => {
                        if (newValue) {
                          setCaseNoMode(newValue);
                          if (newValue === "saved") {
                            setValue("caseNo", []);
                            setCaseNoInputValue("");
                          } else {
                            setValue("caseNoListName", "");
                          }
                        }
                      }}
                    >
                      <StyledToggleButton
                        sx={{
                          textTransform: "none",
                          minWidth: "6rem",
                        }}
                        colors={colors}
                        value="individual"
                      >
                        <Box
                          sx={{
                            display: "flex",
                            gap: "0.5rem",
                            justifyContent: "center",
                            alignItems: "center",
                            flexGrow: 1,
                          }}
                        >
                          {caseNoMode === "individual" && (
                            <CheckIcon
                              sx={{
                                marginBottom: "3%",
                              }}
                              fontSize="small"
                            />
                          )}
                          Individual
                        </Box>
                      </StyledToggleButton>
                      <StyledToggleButton
                        sx={{
                          textTransform: "none",
                          minWidth: "6rem",
                        }}
                        colors={colors}
                        value="saved"
                      >
                        <Box
                          sx={{
                            display: "flex",
                            gap: "0.5rem",
                            justifyContent: "center",
                            alignItems: "center",
                            flexGrow: 1,
                          }}
                        >
                          {caseNoMode === "saved" && (
                            <CheckIcon
                              sx={{
                                marginBottom: "3%",
                              }}
                              fontSize="small"
                            />
                          )}
                         Saved Lists
                        </Box>
                      </StyledToggleButton>
                    </StyledToggleButtonGroup>
                  </Box>
                  {caseNoMode === "individual" ? (
                    <>
                      <Box
                        display="flex"
                        flexDirection="row"
                        flexGrow={1}
                      >
                        <StyledFormControl colors={colors} fullWidth>
                          <Controller
                            name="caseNo"
                            control={control}
                            defaultValue={[]}
                            render={({
                              field: {
                                onChange,
                                value,
                                ref,
                              },
                            }) => (
                              <Autocomplete
                                fullWidth
                                freeSolo
                                clearIcon={<CloseIcon sx={{ color: colors.text.muted }} />}
                                disableClearable
                                multiple
                                inputValue={caseNoInputValue}
                                disableCloseOnSelect
                                onChange={(_, data) => {
                                  onChange(data || []);
                                }}
                                onInputChange={(_, data, reason) => {
                                  if (reason === "input") {
                                    setCaseNoInputValue(data);
                                  }
                                }}
                                value={Array.isArray(value) ? value : []}
                                options={caseNumbers || []}
                                ListboxProps={{
                                  sx: {
                                    maxHeight: "250px",
                                    overflowY: "auto",
                                    fontFamily: "Roboto",
                                    lineHeight: "1.5rem",
                                    backgroundColor: colors.background.paper,
                                    "&.selected": {
                                      backgroundColor:
                                        colors.primary.light,
                                    },
                                    color: colors.text.primary,
                                  },
                                }}
                                slotProps={{
                                  popupIndicator: {
                                    sx: { color: colors.text.muted ,
                                    '&:hover': { color: colors.text.primary },
                                  }
                                  },
                                  clearIndicator: {
                                    sx: {
                                      color: colors.text.muted,
                                      '&:hover': { color: colors.text.primary },
                                    },
                                  },
                                }}
                                renderInput={(params) => (
                                  <StyledTextField colors={colors}
                                    {...params}
                                    label="Case Number"
                                    variant="outlined"
                                    inputRef={ref}
                                  />
                                )}
                                renderTags={() => null}
                              />
                            )}
                          />
                        </StyledFormControl>
                      </Box>
                      <Box
                        display={"flex"}
                        flexDirection={"row"}
                        gap={4}
                        maxWidth={"62.5rem"}
                        flexWrap={"wrap"}
                      >
                        {selectedCaseNo?.map(
                          (caseNo, index) => (
                            <PlaceHolderButton
                              key={index}
                              onClose={() =>
                                handleCaseNoAutoCompleteDeletion(
                                  caseNo
                                )
                              }
                              text={caseNo}
                            />
                          )
                        )}
                      </Box>
                    </>
                  ) : (
                    <StyledFormControl colors={colors} fullWidth>
                      <Controller
                        name="caseNoListName"
                        control={control}
                        defaultValue=""
                        render={({
                          field: { onChange, value },
                        }) => (
                          <Autocomplete
                            fullWidth
                            value={value || null}
                            onChange={(event, newValue) => {
                              onChange(newValue || "");
                            }}
                            options={caseNoListNames}
                            disabled={caseNoListNames.length === 0}
                            ListboxProps={{
                              sx: {
                                maxHeight: "250px",
                                overflowY: "auto",
                                fontFamily: "Roboto",
                                lineHeight: "1.5rem",
                                backgroundColor: colors.background.paper,
                                "&.selected": {
                                  backgroundColor:
                                    colors.primary.light,
                                },
                                color: colors.text.primary,
                              },
                            }}
                            slotProps={{
                              popupIndicator: {
                                sx: { color: colors.text.muted ,
                                '&:hover': { color: colors.text.primary },
                              }
                              },
                              clearIndicator: {
                                sx: {
                                  color: colors.text.muted,
                                  '&:hover': { color: colors.text.primary },
                                },
                              },
                            }}
                            renderInput={(params) => (
                              <StyledTextField colors={colors}
                                {...params}
                                label={
                                  caseNoListNames.length === 0
                                    ? "No saved lists"
                                    : "Select Saved Lists"
                                }
                                variant="outlined"
                              />
                            )}
                          />
                        )}
                      />
                    </StyledFormControl>
                  )}
                </Box>



                <Box
                  display={"flex"}
                  flexDirection={"column"}
                  gap={4}
                  className="serialNoContainer"
                >
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <H8TitleMedium>Serial Number</H8TitleMedium>
                    <StyledToggleButtonGroup
                      colors={colors}
                      value={serialNoMode}
                      exclusive
                      onChange={(e, newValue) => {
                        if (newValue !== null) {
                          setSerialNoMode(newValue);
                          if (newValue === "saved") {
                            setValue("serialNo", []);
                            setSerialNoInputValue("");
                          } else {
                            setValue("serialNoListName", "");
                          }
                        }
                      }}
                    >
                      <StyledToggleButton
                        sx={{
                          textTransform: "none",
                          minWidth: "6rem",
                        }}
                        colors={colors}
                        value="individual"
                      >
                        <Box
                          sx={{
                            display: "flex",
                            gap: "0.5rem",
                            justifyContent: "center",
                            alignItems: "center",
                            flexGrow: 1,
                          }}
                        >
                          {serialNoMode === "individual" && (
                            <CheckIcon
                              sx={{
                                marginBottom: "3%",
                              }}
                              fontSize="small"
                            />
                          )}
                         Individual
                        </Box>
                      </StyledToggleButton>
                      <StyledToggleButton
                        sx={{
                          textTransform: "none",
                          minWidth: "6rem",
                        }}
                        value="saved"
                        colors={colors}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            gap: "0.5rem",
                            justifyContent: "center",
                            alignItems: "center",
                            flexGrow: 1,
                          }}
                        >
                          {serialNoMode === "saved" && (
                            <CheckIcon
                              sx={{
                                marginBottom: "3%",
                              }}
                              fontSize="small"
                            />
                          )}
                       Saved Lists
                        </Box>
                      </StyledToggleButton>
                    </StyledToggleButtonGroup>
                  </Box>
                  {serialNoMode === "individual" ? (
                    <>
                      <Box
                        display="flex"
                        flexDirection="row"
                        flexGrow={1}
                      >
                        <StyledFormControl colors={colors} fullWidth>
                          <Controller
                            name="serialNo"
                            control={control}
                            defaultValue={[]}
                            render={({
                              field: {
                                onChange,
                                value,
                                ref,
                              },
                            }) => (
                              <Autocomplete
                                fullWidth
                                freeSolo
                                clearIcon={<CloseIcon sx={{ color: colors.text.muted }} />}
                                disableClearable
                                multiple
                                inputValue={serialNoInputValue}
                                disableCloseOnSelect
                                onChange={(_, data) => {
                                  onChange(data || []);
                                }}
                                onInputChange={(_, data, reason) => {
                                  if (reason === "input") {
                                    setSerialNoInputValue(data);
                                  }
                                }}
                                value={Array.isArray(value) ? value : []}
                                options={caseSerialNo || []}
                                ListboxProps={{
                                  sx: {
                                    maxHeight: "250px",
                                    overflowY: "auto",
                                    fontFamily: "Roboto",
                                    lineHeight: "1.5rem",
                                    backgroundColor: colors.background.paper,
                                    "&.selected": {
                                      backgroundColor:
                                        colors.primary.light,
                                    },
                                    color: colors.text.primary,
                                  },
                                }}
                                slotProps={{
                                  popupIndicator: {
                                    sx: { color: colors.text.muted ,
                                    '&:hover': { color: colors.text.primary },
                                  }
                                  },
                                  clearIndicator: {
                                    sx: {
                                      color: colors.text.muted,
                                      '&:hover': { color: colors.text.primary },
                                    },
                                  },
                                }}
                                renderInput={(params) => (
                                  <StyledTextField colors={colors}
                                    {...params}
                                    label="Serial Number"
                                    variant="outlined"
                                    inputRef={ref}
                                  />
                                )}
                                renderTags={() => null}
                              />
                            )}
                          />
                        </StyledFormControl>
                      </Box>
                      <Box
                        display={"flex"}
                        flexDirection={"row"}
                        gap={4}
                        maxWidth={"62.5rem"}
                        flexWrap={"wrap"}
                      >
                        {selectedSerialNo?.map(
                          (serialNo, index) => (
                            <PlaceHolderButton
                              key={index}
                              onClose={() =>
                                handleSerialNoAutoCompleteDeletion(
                                  serialNo
                                )
                              }
                              text={serialNo}
                            />
                          )
                        )}
                      </Box>
                    </>
                  ) : (
                    <StyledFormControl colors={colors} fullWidth>
                      <Controller
                        name="serialNoListName"
                        control={control}
                        defaultValue=""
                        render={({
                          field: { onChange, value },
                        }) => (
                          <Autocomplete
                            fullWidth
                            value={value || null}
                            onChange={(event, newValue) => {
                              onChange(newValue || "");
                            }}
                            options={serialNoListNames}
                            disabled={serialNoListNames.length === 0}
                            ListboxProps={{
                              sx: {
                                maxHeight: "250px",
                                overflowY: "auto",
                                fontFamily: "Roboto",
                                lineHeight: "1.5rem",
                                backgroundColor: colors.background.paper,
                                "&.selected": {
                                  backgroundColor:
                                    colors.primary.light,
                                },
                                color: colors.text.primary,
                              },
                            }}
                            slotProps={{
                              popupIndicator: {
                                sx: { color: colors.text.muted ,
                                '&:hover': { color: colors.text.primary },
                              }
                              },
                              clearIndicator: {
                                sx: {
                                  color: colors.text.muted,
                                  '&:hover': { color: colors.text.primary },
                                },
                              },
                            }}
                            renderInput={(params) => (
                              <StyledTextField colors={colors}
                                {...params}
                                label={
                                  serialNoListNames.length === 0
                                    ? "No saved lists"
                                    : "Select Saved Lists"
                                }
                                variant="outlined"
                              />
                            )}
                          />
                        )}
                      />
                    </StyledFormControl>
                  )}
                </Box>

                <Box
                  display={"flex"}
                  flexDirection={"column"}
                  gap={4}
                  className="caseStatusContainer"
                >
                  <H8TitleMedium>Case Status</H8TitleMedium>
                  <Box display="flex" flexDirection="row" flexGrow={1}>
                    <StyledFormControl colors={colors}  fullWidth>
                      <Controller
                        name="caseStatus"
                        control={control}
                        defaultValue={[]}
                        render={({
                          field: { onChange, value, ref },
                        }) => (
                          <Autocomplete
                            fullWidth
                            multiple
                            disableCloseOnSelect
                            options={caseStatusOptions}
                            value={value || []}
                            onChange={(_, data) => onChange(data)}
                            ListboxProps={{
                              sx: {
                                maxHeight: "250px",
                                overflowY: "auto",
                                fontFamily: "Roboto",
                                lineHeight: "1.5rem",
                                backgroundColor: colors.background.paper,
                                "&.selected": {
                                  backgroundColor:
                                    colors.primary.light,
                                },
                                color: colors.text.primary,
                              },
                            }}
                            slotProps={{
                              popupIndicator: {
                                sx: { color: colors.text.muted ,
                                '&:hover': { color: colors.text.primary },
                              }
                              },
                              clearIndicator: {
                                sx: {
                                  color: colors.text.muted,
                                  '&:hover': { color: colors.text.primary },
                                },
                              },
                            }}
                            renderInput={(params) => (
                              <StyledTextField colors={colors}
                                {...params}
                                label="Case Status"
                                variant="outlined"
                                inputRef={ref}
                              />
                            )}
                            renderTags={() => null}
                          />
                        )}
                      />
                    </StyledFormControl>
                  </Box>
                  <Box
                    display={"flex"}
                    flexDirection={"row"}
                    gap={4}
                    maxWidth={"62.5rem"}
                    flexWrap={"wrap"}
                  >
                    {selectedCaseStatus?.map((status, index) => (
                      <PlaceHolderButton
                        key={index}
                        onClose={() =>
                          handleCaseStatusAutoCompleteDeletion(status)
                        }
                        text={status}
                      />
                    ))}
                  </Box>
                </Box>

                <Box
                  display={"flex"}
                  flexDirection={"column"}
                  gap={4}
                  className="issueContainer"
                >
                  <H8TitleMedium>Category</H8TitleMedium>
                  <Box display="flex" flexDirection="row" flexGrow={1}>
                    <StyledFormControl colors={colors} fullWidth>
                      <Controller
                        name="issue"
                        control={control}
                        defaultValue={[]}
                        render={({
                          field: { onChange, value, ref },
                        }) => (
                          <Autocomplete
                            fullWidth
                            multiple
                            disableCloseOnSelect
                            options={issueOptions}
                            value={value || []}
                            onChange={(_, data) => onChange(data)}
                            ListboxProps={{
                              sx: {
                                maxHeight: "250px",
                                overflowY: "auto",
                                fontFamily: "Roboto",
                                lineHeight: "1.5rem",
                                backgroundColor: colors.background.paper,
                                "&.selected": {
                                  backgroundColor:
                                    colors.primary.light,
                                },
                                color: colors.text.primary,
                              },
                            }}
                            slotProps={{
                              popupIndicator: {
                                sx: { color: colors.text.muted ,
                                '&:hover': { color: colors.text.primary },
                              }
                              },
                              clearIndicator: {
                                sx: {
                                  color: colors.text.muted,
                                  '&:hover': { color: colors.text.primary },
                                },
                              },
                            }}
                            renderInput={(params) => (
                              <StyledTextField colors={colors}
                                {...params}
                                label="Category"
                                variant="outlined"
                                inputRef={ref}
                              />
                            )}
                            renderTags={() => null}
                          />
                        )}
                      />
                    </StyledFormControl>
                  </Box>
                  <Box
                    display={"flex"}
                    flexDirection={"row"}
                    gap={4}
                    maxWidth={"62.5rem"}
                    flexWrap={"wrap"}
                  >
                    {selectedIssue?.map((issue, index) => (
                      <PlaceHolderButton
                        key={index}
                        onClose={() =>
                          handleIssueAutoCompleteDeletion(issue)
                        }
                        text={issue}
                      />
                    ))}
                  </Box>
                </Box>

                <Box
                  display={"flex"}
                  flexDirection={"column"}
                  gap={4}
                  className="createdByContainer"
                >
                  <H8TitleMedium>Created By</H8TitleMedium>
                  <Box display="flex" flexDirection="row" flexGrow={1}>
                    <Controller
                      name="createdBy"
                      control={control}
                      defaultValue={[]}
                      render={({ field: { onChange, value, ref } }) => (
                        <Autocomplete
                          fullWidth
                          freeSolo
                          clearIcon={<CloseIcon sx={{ color: colors.text.muted }} />}
                          disableClearable
                          multiple
                          inputValue={createdByInputValue}
                          disableCloseOnSelect
                          onChange={(_, data) => {
                            onChange(data || []);
                          }}
                          onInputChange={(_, data, reason) => {
                            if (reason === "input") {
                              setCreatedByInputValue(data);
                            }
                          }}
                          value={Array.isArray(value) ? value : []}
                          options={caseCreatedBy || []}
                          ListboxProps={{
                            sx: {
                              maxHeight: "250px",
                              overflowY: "auto",
                              fontFamily: "Roboto",
                              lineHeight: "1.5rem",
                              backgroundColor: colors.background.paper,
                              "&.selected": {
                                backgroundColor:
                                  colors.primary.light,
                              },
                              color: colors.text.primary,
                            },
                          }}
                          slotProps={{
                            popupIndicator: {
                              sx: { color: colors.text.muted ,
                              '&:hover': { color: colors.text.primary },
                            }
                            },
                            clearIndicator: {
                              sx: {
                                color: colors.text.muted,
                                '&:hover': { color: colors.text.primary },
                              },
                            },
                          }}
                          renderInput={(params) => (
                            <StyledTextField colors={colors}
                              {...params}
                              label="Created By"
                              variant="outlined"
                              inputRef={ref}
                            />
                          )}
                          renderTags={() => null}
                        />
                      )}
                    />
                  </Box>
                  <Box
                    display={"flex"}
                    flexDirection={"row"}
                    gap={4}
                    maxWidth={"62.5rem"}
                    flexWrap={"wrap"}
                  >
                    {watch("createdBy")?.map(
                      (createdBy, index) => (
                        <PlaceHolderButton
                          key={index}
                          onClose={() =>
                            handleCreatedByAutoCompleteDeletion(
                              createdBy
                            )
                          }
                          text={createdBy}
                        />
                      )
                    )}
                  </Box>
                </Box>

                <Box
                  display={"flex"}
                  flexDirection={"column"}
                  gap={4}
                  className="updatedByContainer"
                >
                  <H8TitleMedium>Updated By</H8TitleMedium>
                  <Box display="flex" flexDirection="row" flexGrow={1}>
                    <Controller
                      name="updatedBy"
                      control={control}
                      defaultValue={[]}
                      render={({ field: { onChange, value, ref } }) => (
                        <Autocomplete
                          fullWidth
                          freeSolo
                          clearIcon={<CloseIcon sx={{ color: colors.text.muted }}   />}
                          disableClearable
                          multiple
                          inputValue={updatedByInputValue}
                          disableCloseOnSelect
                          onChange={(_, data) => {
                            onChange(data || []);
                          }}
                          onInputChange={(_, data, reason) => {
                            if (reason === "input") {
                              setUpdatedByInputValue(data);
                            }
                          }}
                          value={Array.isArray(value) ? value : []}
                          options={caseUpdatedBy || []}
                          ListboxProps={{
                            sx: {
                              maxHeight: "250px",
                              overflowY: "auto",
                              fontFamily: "Roboto",
                              lineHeight: "1.5rem",
                              backgroundColor: colors.background.paper,
                              "&.selected": {
                                backgroundColor:
                                  colors.primary.light,
                              },
                              color: colors.text.primary,
                            },
                          }}
                          slotProps={{
                            popupIndicator: {
                              sx: { color: colors.text.muted ,
                              '&:hover': { color: colors.text.primary },
                            }
                            },
                            clearIndicator: {
                              sx: {
                                color: colors.text.muted,
                                '&:hover': { color: colors.text.primary },
                              },
                            },
                          }}
                          renderInput={(params) => (
                            <StyledTextField colors={colors}
                              {...params}
                              label="Updated By"
                              variant="outlined"
                              inputRef={ref}
                            />
                          )}
                          renderTags={() => null}
                        />
                      )}
                    />
                  </Box>
                  <Box
                    display={"flex"}
                    flexDirection={"row"}
                    gap={4}
                    maxWidth={"62.5rem"}
                    flexWrap={"wrap"}
                  >
                    {watch("updatedBy")?.map(
                      (updatedBy, index) => (
                        <PlaceHolderButton
                          key={index}
                          onClose={() =>
                            handleUpdatedByAutoCompleteDeletion(
                              updatedBy
                            )
                          }
                          text={updatedBy}
                        />
                      )
                    )}
                  </Box>
                </Box>

                <Box
                  display={"flex"}
                  flexDirection={"column"}
                  gap={4}
                  className="summaryContainer"
                >
                  <H8TitleMedium>Summary</H8TitleMedium>
                  <Box display="flex" flexDirection="row" flexGrow={1}>
                    <Controller
                      name="summary"
                      control={control}
                      defaultValue={[]}
                      render={({ field: { onChange, value, ref } }) => (
                        <Autocomplete
                          fullWidth
                          freeSolo
                          clearIcon={<CloseIcon sx={{ color: colors.text.muted }}   />}
                          disableClearable
                          multiple
                          inputValue={summaryInputValue}
                          disableCloseOnSelect
                          onChange={(_, data) => {
                            onChange(data || []);
                          }}
                          onInputChange={(_, data, reason) => {
                            if (reason === "input") {
                              setSummaryInputValue(data);
                            }
                          }}
                          value={Array.isArray(value) ? value : []}
                          options={summaryData || []}
                          ListboxProps={{
                            sx: {
                              maxHeight: "250px",
                              overflowY: "auto",
                              fontFamily: "Roboto",
                              lineHeight: "1.5rem",
                              backgroundColor: colors.background.paper,
                              "&.selected": {
                                backgroundColor:
                                  colors.primary.light,
                              },
                              color: colors.text.primary,
                            },
                          }}
                          slotProps={{
                            popupIndicator: {
                              sx: { color: colors.text.muted ,
                              '&:hover': { color: colors.text.primary },
                            }
                            },
                            clearIndicator: {
                              sx: {
                                color: colors.text.muted,
                                '&:hover': { color: colors.text.primary },
                              },
                            },
                          }}
                          renderInput={(params) => (
                            <StyledTextField colors={colors}
                              {...params}
                              label="Summary"
                              variant="outlined"
                              inputRef={ref}
                            />
                          )}
                          renderTags={() => null}
                        />
                      )}
                    />
                  </Box>
                  <Box
                    display={"flex"}
                    flexDirection={"row"}
                    gap={4}
                    maxWidth={"62.5rem"}
                    flexWrap={"wrap"}
                  >
                    {watch("summary")?.map(
                      (summary, index) => (
                        <PlaceHolderButton
                          key={index}
                          onClose={() =>
                            handleSummaryAutoCompleteDeletion(
                              summary
                            )
                          }
                          text={summary}
                        />
                      )
                    )}
                  </Box>
                </Box>

                <Box
                  display={"flex"}
                  flexDirection={"column"}
                  gap={4}
                  className="firmwareContainer"
                >
                  <H8TitleMedium>Firmware</H8TitleMedium>
                  <Box display="flex" flexDirection="row" flexGrow={1}>
                    <Controller
                      name="firmware"
                      control={control}
                      defaultValue={[]}
                      render={({ field: { onChange, value, ref } }) => (
                        <Autocomplete
                          fullWidth
                          freeSolo
                          clearIcon={<CloseIcon sx={{ color: colors.text.muted }}   />}
                          disableClearable
                          multiple
                          inputValue={firmwareInputValue}
                          disableCloseOnSelect
                          onChange={(_, data) => {
                            onChange(data || []);
                          }}
                          onInputChange={(_, data, reason) => {
                            if (reason === "input") {
                              setFirmwareInputValue(data);
                            }
                          }}
                          value={Array.isArray(value) ? value : []}
                          options={firmwareData || []}
                          ListboxProps={{
                            sx: {
                              maxHeight: "250px",
                              overflowY: "auto",
                              fontFamily: "Roboto",
                              lineHeight: "1.5rem",
                              backgroundColor: colors.background.paper,
                              "&.selected": {
                                backgroundColor:
                                  colors.primary.light,
                              },
                              color: colors.text.primary,
                            },
                          }}
                          slotProps={{
                            popupIndicator: {
                              sx: { color: colors.text.muted ,
                              '&:hover': { color: colors.text.primary },
                            }
                            },
                            clearIndicator: {
                              sx: {
                                color: colors.text.muted,
                                '&:hover': { color: colors.text.primary },
                              },
                            },
                          }}
                          renderInput={(params) => (
                            <StyledTextField colors={colors}
                              {...params}
                              label="Firmware"
                              variant="outlined"
                              inputRef={ref}
                            />
                          )}
                          renderTags={() => null}
                        />
                      )}
                    />
                  </Box>
                  <Box
                    display={"flex"}
                    flexDirection={"row"}
                    gap={4}
                    maxWidth={"62.5rem"}
                    flexWrap={"wrap"}
                  >
                    {watch("firmware")?.map(
                      (firmware, index) => (
                        <PlaceHolderButton
                          key={index}
                          onClose={() =>
                            handleFirmwareAutoCompleteDeletion(
                              firmware
                            )
                          }
                          text={firmware}
                        />
                      )
                    )}
                  </Box>
                </Box>
              </Box>
              <Box
                display={"flex"}
                flexDirection={"row"}
                gap={4}
                justifyContent={"flex-end"}
                paddingTop={4}
              >
                <SecondaryButton colors={colors} onClick={ClearFilters}>
                  Clear
                </SecondaryButton>
                <PrimaryButton colors={colors} type="submit">
                  Apply
                </PrimaryButton>
              </Box>
            </form>
          </Box>
        )}
        {selectedTab === 1 && (
          <CaseManagementSavedLists
            tabIndex={selectedTab}
            filterNames={filterNames}
            refetchFilterNames={refetchFilterNames}
            areFilterNamesLoading={areFilterNamesLoading}
            getValue={getValues}
            setFilterValue={setValue}
          />
        )}
      </DialogContent>
    </StyledDialog>
  );
};

const CaseManagementFilters = (props) => {
  const { colors } = useTheme();
  const [openFilterModal, setOpenFilter] = useState(false);
  const [activeFilters, setActiveFilters] = useState({});

  // Get filters from session storage for badge count
  const getFiltersFromSession = () => {
    try {
      const filters = sessionStorage.getItem("caseFilters");
      return filters ? JSON.parse(filters) : {};
    } catch (error) {
      return {};
    }
  };

  const groupFilters = getFiltersFromSession();

  // Only show badge count for applied filters (groupFilters), not form values (activeFilters)
  // This matches Miner Inventory behavior - badge only updates after clicking "Apply"
  const badgeContent = Object.entries(groupFilters).filter(
    ([key, value]) =>
      !(
        value === undefined ||
        value === null ||
        value === "" ||
        (Array.isArray(value) && value.length === 0)
      )
  ).length;

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {badgeContent > 0 && (
          <SecondaryButton colors={colors}
            size="small"
            onClick={() => {
              // Clear all filters
              const newFilters = {};

              // Update URL parameters - clear all
              const url = new URL(window.location.href);
              url.search = ""; // Clear all search parameters

              // Update browser URL
              window.history.replaceState({}, "", url);

              // Update session storage
              sessionStorage.setItem("caseFilters", JSON.stringify(newFilters));

              // Dispatch custom event to notify that filters have changed
              window.dispatchEvent(new CustomEvent('caseFiltersChanged', {
                detail: { filters: newFilters }
              }));
            }}
            sx={{ fontSize: '0.75rem', padding: '4px 8px' }}
          >
            Clear Filters
          </SecondaryButton>
        )}
        <Tooltip title="Filters">
          <span>
            <CustomBadge badgeContent={badgeContent} colors={colors}>
              <StyledIconButton colors={colors}
                open={openFilterModal}
                sx={{}}
                onClick={() => setOpenFilter(true)}
              >
                <FilterList
                  size="small"
                />
              </StyledIconButton>
            </CustomBadge>
          </span>
        </Tooltip>
      </Box>
      <FilterModal
        open={openFilterModal}
        setOpenFilter={setOpenFilter}
        setActiveFilters={setActiveFilters}
      />
    </>
  );
};

export default CaseManagementFilters;
